import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  setUnregisteredStudents,
  setRegisteredStudents,
  setStudentsLoading,
  setStudentsError,
  setLastRegInfo,
  setCalculation,
  setCalcLoading,
  setCampDates,
  setCampDatesLoading,
  setSelectedCamp,
  setPaymentInfo,
  clearCalculation,
  clearCampData,
} from '../../../redux/slices/campSlice';
import campService from '../services/campService';
import Toast from '../../../components/Toast';
import './CampRegistration.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateShort(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function generateWeeks(startDateIso, endDateIso, feePerWeek) {
  if (!startDateIso || !endDateIso) return [];
  const weeks = [];
  let current = new Date(startDateIso);
  const end = new Date(endDateIso);
  let weekNum = 1;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  while (current < end) {
    const weekStart = new Date(current);
    const weekEnd = new Date(current);
    weekEnd.setDate(weekEnd.getDate() + 6);
    if (weekEnd > end) weekEnd.setTime(end.getTime());

    const isExpired = weekEnd < today;
    weeks.push({
      weekNumber: weekNum,
      startDate: weekStart.toISOString(),
      endDate: weekEnd.toISOString(),
      noOfDays: Math.ceil((weekEnd - weekStart) / (1000 * 60 * 60 * 24)) + 1,
      fee: feePerWeek || 0,
      month: weekStart.toLocaleString('default', { month: 'long', year: 'numeric' }),
      label: `Week ${weekNum}`,
      dateRange: `${formatDateShort(weekStart.toISOString())} – ${formatDate(weekEnd.toISOString())}`,
      isExpired,
    });

    current.setDate(current.getDate() + 7);
    weekNum++;
  }
  return weeks;
}

function getStudentName(s) {
  const fn = s.student_First_Name || s.firstName || '';
  const ln = s.student_Last_Name || s.lastName || '';
  return `${fn} ${ln}`.trim() || 'Student';
}

function getStudentInitials(s) {
  const fn = (s.student_First_Name || s.firstName || '?')[0] || '?';
  const ln = (s.student_Last_Name || s.lastName || '')[0] || '';
  return (fn + ln).toUpperCase();
}

function getStudentId(s) {
  return s.student_Id || s.studentId || s.Student_Id || 0;
}

// ─── Avatar helper (same as EnrollStudent) ────────────────────────────────────
function getAvatarImage(gender) {
  const g = gender?.toLowerCase()?.trim();
  if (g === 'male' || g === 'm') {
    return `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><defs><linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style="stop-color:#60a5fa"/><stop offset="100%" style="stop-color:#3b82f6"/></linearGradient><filter id="s"><feDropShadow dx="0" dy="4" stdDeviation="8" flood-opacity="0.3"/></filter></defs><circle cx="100" cy="100" r="95" fill="url(#bg)" filter="url(#s)"/><text x="100" y="145" font-size="140" text-anchor="middle" font-family="Arial">👦</text></svg>`)}`;
  }
  if (g === 'female' || g === 'f') {
    return `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><defs><linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style="stop-color:#f9a8d4"/><stop offset="100%" style="stop-color:#ec4899"/></linearGradient><filter id="s"><feDropShadow dx="0" dy="4" stdDeviation="8" flood-opacity="0.3"/></filter></defs><circle cx="100" cy="100" r="95" fill="url(#bg)" filter="url(#s)"/><text x="100" y="145" font-size="140" text-anchor="middle" font-family="Arial">👧</text></svg>`)}`;
  }
  return `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><circle cx="100" cy="100" r="95" fill="#f3f4f6"/><text x="100" y="145" font-size="140" text-anchor="middle" font-family="Arial">👤</text></svg>`)}`;
}

// ─── Main Component ────────────────────────────────────────────────────────────

const CampRegistration = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const campId = searchParams.get('campId');

  const { user } = useSelector((state) => state.auth);
  const userId = user?.User_Id || user?.user_Id || user?.userId;
  const {
    paymentInfo,
    selectedCamp,
    campDates,
    unregisteredStudents,
    registeredStudents,
    studentsLoading,
    studentsError,
    lastRegInfo,
    calculation,
    calcLoading,
  } = useSelector((state) => state.camp);

  const allStudents = React.useMemo(() => {
    const regIds = new Set(registeredStudents.map((s) => getStudentId(s)));
    const combined = [...registeredStudents];
    unregisteredStudents.forEach((s) => {
      if (!regIds.has(getStudentId(s))) combined.push(s);
    });
    return combined;
  }, [unregisteredStudents, registeredStudents]);

  // Camp sessions from DB (must be declared before weeks useMemo to avoid TDZ)
  const [campSessions, setCampSessions] = useState([]);

  const fallbackFee = parseFloat(
    selectedCamp?.camp_Fee || selectedCamp?.Camp_Fee ||
    campDates?.camp_Fee || campDates?.Camp_Fee || 250
  );
  const fallbackExtFee = parseFloat(
    campDates?.accommodation_Fee || campDates?.Accommodation_Fee ||
    campDates?.accommodationFee || 75
  );

  const weeks = React.useMemo(() => {
    if (campSessions.length > 0) {
      return campSessions.map((s, idx) => {
        const startIso = s.camp_Start_Date || s.Camp_Start_Date;
        const endIso   = s.camp_End_Date   || s.Camp_End_Date;
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const weekEnd = new Date(endIso);
        return {
          weekNumber: s.camp_Session_Number || s.Camp_Session_Number || (idx + 1),
          startDate:  startIso,
          endDate:    endIso,
          noOfDays:   Math.ceil((weekEnd - new Date(startIso)) / (1000 * 60 * 60 * 24)) + 1,
          fee:        parseFloat(s.camp_Session_Fee || s.Camp_Session_Fee || fallbackFee),
          extFee:     parseFloat(s.extended_Session_Fee || s.Extended_Session_Fee || fallbackExtFee),
          month:      new Date(startIso).toLocaleString('default', { month: 'long', year: 'numeric' }),
          label:      `Week ${s.camp_Session_Number || s.Camp_Session_Number || (idx + 1)}`,
          dateRange:  `${formatDateShort(startIso)} – ${formatDate(endIso)}`,
          isExpired:  weekEnd < today,
        };
      });
    }
    if (!campDates) return [];
    return generateWeeks(
      campDates.campStartDate || campDates.CampStartDate,
      campDates.campEndDate   || campDates.CampEndDate,
      fallbackFee
    ).map(w => ({ ...w, extFee: fallbackExtFee }));
  }, [campSessions, campDates, selectedCamp]);

  const weekFeeFromDB = weeks.length > 0 ? weeks[0].fee : fallbackFee;
  const extFeeFromDB  = weeks.length > 0 ? weeks[0].extFee : fallbackExtFee;

  // Modal: week selection
  const [weekModalStudent, setWeekModalStudent] = useState(null);
  const [existingRecords, setExistingRecords] = useState([]);
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [extendedStayWeeks, setExtendedStayWeeks] = useState(new Set());
  const [drawerStep, setDrawerStep] = useState('select'); // 'select' | 'summary'
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [savingWeeks, setSavingWeeks] = useState(false);
  const [weekSaveError, setWeekSaveError] = useState(null);

  // Modal: add/edit student
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [studentModalMode, setStudentModalMode] = useState('add');
  const [editingStudent, setEditingStudent] = useState(null);
  const [studentForm, setStudentForm] = useState({ firstName: '', lastName: '', dateOfBirth: '', gender: '', relationship: '' });
  const [studentFormErrors, setStudentFormErrors] = useState({});
  const [savingStudent, setSavingStudent] = useState(false);

  // Free confirm / success
  const [confirmingFree, setConfirmingFree] = useState(false);
  const [showFreeSuccess, setShowFreeSuccess] = useState(false);

  // Multi-student batch enrollment
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());
  const [studentQueue, setStudentQueue] = useState([]);
  const [currentQueueIdx, setCurrentQueueIdx] = useState(0);
  const [enrollmentPlan, setEnrollmentPlan] = useState([]);

  // Toast
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  const showToastMsg = (msg, type = 'success') => {
    setToastMessage(msg);
    setToastType(type);
    setShowToast(true);
  };

  const fetchingRef = useRef(false);

  // ── Body scroll lock when drawer is open ───────────────────────────────────
  useEffect(() => {
    if (weekModalStudent) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [weekModalStudent]);

  // ── Restore selection + auto-open when returning from payment ───────────────
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (!paymentInfo?.students?.length) return;
    if (!allStudents.length) return;
    autoOpenedRef.current = true;
    // Restore selected tiles
    const ids = new Set(paymentInfo.students.map(s => s.studentId));
    setSelectedStudentIds(ids);
    // Auto-open drawer for first student
    const first = allStudents.find(s => ids.has(getStudentId(s)));
    if (first) handleOpenWeekModal(first);
  }, [allStudents]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── On mount: load camp dates + students ───────────────────────────────────
  useEffect(() => {
    if (!campId) return;
    if (!campDates) fetchCampDates();
    if (!selectedCamp) fetchCampInfoIfMissing();
    if (userId) loadStudents();
  }, [campId, userId]);

  const fetchCampInfoIfMissing = async () => {
    const info = await campService.getCampInfo(campId);
    if (info) dispatch(setSelectedCamp(info));
  };

  const fetchCampDates = async () => {
    try {
      dispatch(setCampDatesLoading(true));
      const [data, sessions] = await Promise.all([
        campService.getAccommodationAndCampDate(campId),
        campService.getCampSessions(campId),
      ]);
      if (data && (Array.isArray(data) ? data.length > 0 : true)) {
        dispatch(setCampDates(Array.isArray(data) ? data[0] : data));
      }
      if (Array.isArray(sessions) && sessions.length > 0) {
        setCampSessions(sessions);
      }
    } catch (err) {
      console.error('Failed to load camp dates:', err);
    } finally {
      dispatch(setCampDatesLoading(false));
    }
  };

  const loadStudents = async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    dispatch(setStudentsLoading(true));
    dispatch(setStudentsError(null));
    try {
      let unreg, reg;
      try {
        [unreg, reg] = await Promise.all([
          campService.getUnregisteredStudents(userId, campId),
          campService.getRegisteredStudents(userId, campId),
        ]);
      } catch {
        await new Promise((r) => setTimeout(r, 1500));
        [unreg, reg] = await Promise.all([
          campService.getUnregisteredStudents(userId, campId),
          campService.getRegisteredStudents(userId, campId),
        ]);
      }
      dispatch(setUnregisteredStudents(unreg || []));
      dispatch(setRegisteredStudents(reg || []));
    } catch (err) {
      dispatch(setStudentsError('Failed to load students. Please try again.'));
      console.error(err);
    } finally {
      dispatch(setStudentsLoading(false));
      fetchingRef.current = false;
    }
  };

  const fetchCalculation = async () => {
    dispatch(setCalcLoading(true));
    try {
      const calc = await campService.getCalculation(userId, campId);
      dispatch(setCalculation(Array.isArray(calc) ? calc[0] : calc));
    } catch (err) {
      console.error('Failed to fetch calculation:', err);
      dispatch(setCalcLoading(false));
    }
  };

  // ── Week modal open ────────────────────────────────────────────────────────
  const handleOpenWeekModal = async (student) => {
    setWeekModalStudent(student);
    setWeekSaveError(null);
    setSelectedWeeks([]);
    setExtendedStayWeeks(new Set());
    setDrawerStep('select');
    const sid = getStudentId(student);
    if (!sid) return;
    setLoadingRecords(true);
    try {
      const toDateOnly = (d) => { try { return new Date(d).toISOString().split('T')[0]; } catch { return ''; } };

      // Use VisitRegisteredStudent (Retreat_Registration_Details) as the source
      const retreatRaw = await campService.getSessionRecords(campId, sid);
      const rawArr = Array.isArray(retreatRaw) ? retreatRaw : [];
      // Group by week number: merge duplicates with OR for paid + OR for extended stay
      const weekMap = {};
      for (const r of rawArr) {
        // Prefer Visits field (week number already stored); fall back to date matching
        let wn = parseInt(r.visits || r.Visits || '0');
        if (!wn) {
          const recDate = toDateOnly(r.start_Date || r.Start_Date || '');
          const matchedWeek = weeks.find((w) => toDateOnly(w.startDate) === recDate);
          wn = matchedWeek ? matchedWeek.weekNumber : 0;
        }
        if (!wn) continue;
        const recPaid = r.registration_Completed || r.Registration_Completed || 0;
        if (!weekMap[wn]) {
          weekMap[wn] = {
            ...r,
            visits: String(wn),
            Visits: String(wn),
            registration_Completed: recPaid,
            Registration_Completed: recPaid,
            Is_Accommodation_Needed: 'N',
            is_Accommodation_Needed: 'N',
          };
        } else {
          // OR logic: if any row for this week is paid, mark as paid
          if (recPaid) {
            weekMap[wn].registration_Completed = recPaid;
            weekMap[wn].Registration_Completed = recPaid;
          }
        }
        // OR logic: if any duplicate row for this week has extended stay, mark Y
        if ((r.Is_Accommodation_Needed || r.is_Accommodation_Needed) === 'Y') {
          weekMap[wn].Is_Accommodation_Needed = 'Y';
          weekMap[wn].is_Accommodation_Needed = 'Y';
        }
      }
      const dedupedArr = Object.values(weekMap).filter((r) => parseInt(r.visits) > 0);

      setExistingRecords(dedupedArr);

      // Always restore extended stay from DB records
      const extSet = new Set(
        dedupedArr
          .filter((r) => (r.Is_Accommodation_Needed || r.is_Accommodation_Needed) === 'Y')
          .map((r) => parseInt(r.visits))
          .filter((wn) => wn > 0)
      );
      setExtendedStayWeeks(extSet);

      if (dedupedArr.length > 0) {
        // If all available weeks are paid — don't open modal
        const paidWeekNums = dedupedArr
          .filter((r) => !!(r.registration_Completed || r.Registration_Completed))
          .map((r) => parseInt(r.visits))
          .filter((wn) => wn > 0);
        if (paidWeekNums.length >= weeks.length && weeks.length > 0) {
          setWeekModalStudent(null);
          setLoadingRecords(false);
          showToastMsg('All weeks are already registered for this student.', 'info');
          return;
        }
      }

      // Restore selections only from paymentInfo (back navigation from payment page).
      // Never auto-select DB pending weeks — selections should be explicit per session.
      const studentEntry = paymentInfo?.students?.find(s => s.studentId === parseInt(sid))
        || (paymentInfo?.studentId === parseInt(sid) ? paymentInfo : null);
      if (studentEntry?.selectedWeekNumbers?.length > 0) {
        setSelectedWeeks(studentEntry.selectedWeekNumbers);
        setExtendedStayWeeks(new Set([...extSet, ...(studentEntry.extendedWeekNumbers || [])]));
      }
    } catch (err) {
      setExistingRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  };

  const handleCloseWeekModal = () => {
    setWeekModalStudent(null);
    setExistingRecords([]);
    setSelectedWeeks([]);
    setExtendedStayWeeks(new Set());
    setDrawerStep('select');
    setWeekSaveError(null);
  };

  const toggleStudentSelection = (student) => {
    const sid = getStudentId(student);
    setSelectedStudentIds(prev => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid); else next.add(sid);
      return next;
    });
  };

  const clearStudentSelection = () => {
    setSelectedStudentIds(new Set());
    setStudentQueue([]);
    setCurrentQueueIdx(0);
    setEnrollmentPlan([]);
  };

  const handleStartBatchEnrollment = () => {
    const queue = allStudents.filter(s => selectedStudentIds.has(getStudentId(s)));
    if (queue.length === 0) return;
    setStudentQueue(queue);
    setCurrentQueueIdx(0);
    setEnrollmentPlan([]);
    handleOpenWeekModal(queue[0]);
  };

  const toggleExtendedStay = (weekNumber) => {
    setExtendedStayWeeks((prev) => {
      const next = new Set(prev);
      if (next.has(weekNumber)) next.delete(weekNumber);
      else next.add(weekNumber);
      return next;
    });
  };

  const toggleWeek = (weekNumber) => {
    const isSelected = selectedWeeks.includes(weekNumber);
    setSelectedWeeks((prev) =>
      isSelected ? prev.filter((w) => w !== weekNumber) : [...prev, weekNumber]
    );
    if (isSelected) {
      setExtendedStayWeeks((prev) => {
        const next = new Set(prev);
        next.delete(weekNumber);
        return next;
      });
    }
  };

  const isPaidWeek = (weekNumber) =>
    existingRecords.some(
      (r) =>
        parseInt(r.visits || r.Visits || '0') === weekNumber &&
        (r.registration_Completed || r.Registration_Completed)
    );

  const isExistingPendingWeek = (weekNumber) =>
    existingRecords.some(
      (r) =>
        parseInt(r.visits || r.Visits || '0') === weekNumber &&
        !(r.registration_Completed || r.Registration_Completed)
    );

  // ── Save weeks ─────────────────────────────────────────────────────────────
  const handleSaveWeeks = async () => {
    if (!weekModalStudent || selectedWeeks.length === 0) {
      setWeekSaveError('Please select at least one week.');
      return;
    }
    setSavingWeeks(true);
    setWeekSaveError(null);
    const sid = getStudentId(weekModalStudent);

    // Re-fetch fresh session records to prevent duplicate week registrations
    let freshRecords = existingRecords;
    try {
      const rawFresh = await campService.getSessionRecords(campId, sid);
      const rawArr = Array.isArray(rawFresh) ? rawFresh : [];
      const wMap = {};
      for (const r of rawArr) {
        const wn = parseInt(r.visits || r.Visits || '0');
        if (!wn) continue;
        const paid = r.registration_Completed || r.Registration_Completed || 0;
        if (!wMap[wn]) wMap[wn] = { ...r, visits: String(wn), registration_Completed: paid, Registration_Completed: paid };
        else if (paid) { wMap[wn].registration_Completed = paid; wMap[wn].Registration_Completed = paid; }
      }
      freshRecords = Object.values(wMap).filter(r => parseInt(r.visits) > 0);
      setExistingRecords(freshRecords);
    } catch { /* fall back to existing state */ }

    const isFreshPaidWeek = (wn) => freshRecords.some(r => parseInt(r.visits) === wn && (r.registration_Completed || r.Registration_Completed));
    const isFreshPendingWeek = (wn) => freshRecords.some(r => parseInt(r.visits) === wn && !(r.registration_Completed || r.Registration_Completed));

    // Guard: block if every selected week is already paid
    if (selectedWeeks.length > 0 && selectedWeeks.every(wn => isFreshPaidWeek(wn))) {
      setSavingWeeks(false);
      setWeekSaveError('All selected weeks are already registered and paid for this student.');
      return;
    }

    // Compute grand total from current drawer selections (use per-week actual fee)
    const newSel = selectedWeeks.filter((w) => !isFreshPaidWeek(w));
    const subtotal = newSel.reduce((sum, wn) => {
      const wk = weeks.find(w => w.weekNumber === wn);
      return sum + (wk?.fee ?? weekFeeFromDB);
    }, 0);
    const discountAmt = newSel.length > 1 ? subtotal * 0.10 : 0;
    const extCount = [...extendedStayWeeks].filter((w) => !isFreshPaidWeek(w)).length;
    const extTotal = [...extendedStayWeeks].filter((w) => !isFreshPaidWeek(w)).reduce((sum, wn) => {
      const wk = weeks.find(w => w.weekNumber === wn);
      return sum + (wk?.extFee ?? extFeeFromDB);
    }, 0);
    const grandTotal = subtotal - discountAmt + extTotal;

    try {
      // Step 1: Create registration header (only if there are truly new weeks to add)
      const newWeeks = selectedWeeks.filter((wn) => !isFreshPaidWeek(wn) && !isFreshPendingWeek(wn));
      let registrationId;

      if (newWeeks.length === 0) {
        // All selected weeks already in DB as pending — reuse existing registrationId
        const existingEntry = paymentInfo?.students?.find(s => s.studentId === parseInt(sid))
          || (paymentInfo?.studentId === parseInt(sid) ? paymentInfo : null);
        // Try Redux first, then fall back to existingRecords from DB
        const recordId = existingRecords[0]?.Retreat_Registration_Id
          || existingRecords[0]?.retreat_Registration_Id
          || existingRecords[0]?.registrationId;
        registrationId = existingEntry?.registrationId || lastRegInfo?.registrationId || recordId || 0;
        if (!registrationId) {
          // Last resort: create a fresh registration
          const regResult = await campService.addRegistration(userId, campId, sid);
          registrationId =
            regResult?.retreat_Registration_Id ||
            regResult?.Retreat_Registration_Id ||
            regResult?.registrationId ||
            (Array.isArray(regResult) && regResult[0]?.retreat_Registration_Id) ||
            0;
        }
      } else {
        const regResult = await campService.addRegistration(userId, campId, sid);
        registrationId =
          regResult?.retreat_Registration_Id ||
          regResult?.Retreat_Registration_Id ||
          regResult?.registrationId ||
          (Array.isArray(regResult) && regResult[0]?.retreat_Registration_Id) ||
          0;
      }

      // Step 2: Add detail for each newly selected week — parallel for speed
      await Promise.all(newWeeks.map((weekNum) => {
        const weekData = weeks.find((w) => w.weekNumber === weekNum);
        if (!weekData) return Promise.resolve();
        const hasExtended = extendedStayWeeks.has(weekNum);
        return campService.addRegistrationDetail(registrationId, campId, sid, {
          ...weekData,
          isAccommodationNeeded: hasExtended ? 'Y' : 'N',
          fee: weekData.fee,
          accommodationFees: hasExtended ? (weekData.extFee ?? extFeeFromDB) : 0,
        }, userId);
      }));

      // Store reg info
      dispatch(setLastRegInfo({ studentId: sid, registrationId }));

      if (grandTotal <= 0) {
        // Free registration: stay on page, show free-confirm button
        await fetchCalculation();
        await loadStudents();
        handleCloseWeekModal();
      } else {
        // Paid: build entry for this student
        let resolvedCampName = selectedCamp?.camp_Name || selectedCamp?.Camp_Name || selectedCamp?.camp_name || selectedCamp?.campName || '';
        if (!resolvedCampName) {
          try {
            const campInfo = await campService.getCampInfo(campId);
            resolvedCampName = campInfo?.camp_Name || campInfo?.Camp_Name || campInfo?.camp_name || '';
            if (campInfo) dispatch(setSelectedCamp(campInfo));
          } catch (_) {}
        }
        const studentFullName = weekModalStudent ? getStudentName(weekModalStudent) : '';
        const studentGender = weekModalStudent?.student_Gender || weekModalStudent?.Student_Gender || '';
        const extWeekNums = newSel.filter((wn) => extendedStayWeeks.has(wn));
        const selectedWeeksData = newSel.map((wn) => {
          const wd = weeks.find((w) => w.weekNumber === wn);
          return wd ? { weekNumber: wn, label: wd.label, dateRange: wd.dateRange, fee: wd.fee, extFee: wd.extFee ?? extFeeFromDB, hasExtended: extendedStayWeeks.has(wn) } : null;
        }).filter(Boolean);

        // isFirstTimeForCamp: true when student has no previously PAID records at this camp
        const hadPriorPaid = freshRecords.some(r => !!(r.registration_Completed || r.Registration_Completed));
        const entry = {
          studentId: parseInt(sid),
          studentName: studentFullName,
          studentGender,
          registrationId,
          selectedWeeksData,
          extendedWeekNumbers: extWeekNums,
          selectedWeekNumbers: newSel,
          weekFee: weekFeeFromDB,
          extFee: extFeeFromDB,
          grandTotal,
          isFirstTimeForCamp: !hadPriorPaid,
        };

        if (studentQueue.length > 0 && currentQueueIdx < studentQueue.length - 1) {
          // Batch mode: more students remain — save entry and open next student
          const nextIdx = currentQueueIdx + 1;
          setEnrollmentPlan(prev => [...prev, entry]);
          setCurrentQueueIdx(nextIdx);
          await handleOpenWeekModal(studentQueue[nextIdx]);
        } else {
          // Last or single student: combine all entries and navigate to payment
          handleCloseWeekModal();
          const allEntries = studentQueue.length > 0 ? [...enrollmentPlan, entry] : [entry];
          const totalAmt = allEntries.reduce((sum, e) => sum + e.grandTotal, 0);

          dispatch(setPaymentInfo({
            campId: parseInt(campId),
            userId: parseInt(userId),
            campName: resolvedCampName || `Camp ${campId}`,
            totalAmount: totalAmt,
            // Multi-student array (used by payment page)
            students: allEntries,
            // Single-student compat fields (first student, for backward compat)
            studentId: allEntries[0].studentId,
            studentName: allEntries.length === 1 ? allEntries[0].studentName : `${allEntries.length} Students`,
            studentGender: allEntries[0].studentGender,
            registrationId: allEntries[0].registrationId,
            selectedWeeksData: allEntries[0].selectedWeeksData,
            extendedWeekNumbers: allEntries[0].extendedWeekNumbers,
            selectedWeekNumbers: allEntries[0].selectedWeekNumbers,
            weekFee: allEntries[0].weekFee,
            extFee: allEntries[0].extFee,
            weeksLabel: allEntries[0].selectedWeeksData.map(w => `Week ${w.weekNumber} (${w.dateRange})`).join(', '),
            weekCount: allEntries.reduce((s, e) => s + e.selectedWeekNumbers.length, 0),
            extendedCount: allEntries.reduce((s, e) => s + e.extendedWeekNumbers.length, 0),
            userEmail: user?.User_Email || user?.user_Email || user?.email || '',
            userFirstName: user?.User_First_Name || user?.user_First_Name || '',
            userLastName: user?.User_Last_Name || user?.user_Last_Name || '',
            userPhone: user?.User_Phone || user?.user_Phone || user?.phone || '',
          }));
          if (studentQueue.length > 0) clearStudentSelection();
          navigate(`/camp/payment?campId=${campId}`);
        }
      }
    } catch (err) {
      setWeekSaveError('Failed to save weeks. Please try again.');
      console.error(err);
    } finally {
      setSavingWeeks(false);
    }
  };

  // ── Proceed to payment ─────────────────────────────────────────────────────
  const handleProceedToPayment = () => {
    const totalDue = parseFloat(
      calculation?.net_To_Pay || calculation?.netToPay || calculation?.current_Due_Total || 0
    );
    // Preserve students set by the week modal; if missing, build a minimal entry
    const existingStudents = paymentInfo?.students?.length > 0
      ? paymentInfo.students
      : (() => {
          const sid = lastRegInfo?.studentId;
          if (!sid) return [];
          const registeredIds = new Set(registeredStudents.map((s) => getStudentId(s)));
          return [{ studentId: parseInt(sid), isFirstTimeForCamp: !registeredIds.has(parseInt(sid)) }];
        })();
    dispatch(
      setPaymentInfo({
        campId: parseInt(campId),
        userId: parseInt(userId),
        registrationId: lastRegInfo?.registrationId || 0,
        totalAmount: totalDue,
        campName: selectedCamp?.camp_Name || selectedCamp?.Camp_Name || selectedCamp?.camp_name || selectedCamp?.campName || `Camp ${campId}`,
        userEmail: user?.User_Email || user?.user_Email || user?.email || '',
        userFirstName: user?.User_First_Name || user?.user_First_Name || '',
        userLastName: user?.User_Last_Name || user?.user_Last_Name || '',
        userPhone: user?.User_Phone || user?.user_Phone || user?.phone || '',
        students: existingStudents,
      })
    );
    navigate(`/camp/payment?campId=${campId}`);
  };

  // ── Free registration confirm ──────────────────────────────────────────────
  const handleFreeConfirm = async () => {
    if (!lastRegInfo) return;
    setConfirmingFree(true);

    const timestamp = new Date().toLocaleString();
    const userName = `${user?.User_First_Name || ''} ${user?.User_Last_Name || ''}`.trim();
    const userEmail = user?.User_Email || user?.user_Email || user?.email || '';
    const resolvedCampName = selectedCamp?.camp_Name || selectedCamp?.Camp_Name || selectedCamp?.camp_name || selectedCamp?.campName || `Camp ${campId}`;
    const campDateRange = campDates
      ? `${formatDate(campDates.campStartDate || campDates.CampStartDate)} – ${formatDate(campDates.campEndDate || campDates.CampEndDate)}`
      : '';

    const stepStatus = {
      'Create Order': { success: false, message: 'Pending…' },
      'Confirmation': { success: false, message: 'Pending…' },
      'Email':        { success: false, message: 'Pending…' },
    };

    try {
      let campOrderId = 0;
      let campPaymentId = 0;

      // Step 1: Create order record
      try {
        const orderResp = await campService.createCampOrder({
          userId: parseInt(userId),
          campId: parseInt(campId),
          totalAmount: 0,
          feeAmount: 0,
          discountAmount: 0,
          donationAmount: 0,
          accommodationAmount: 0,
          otherDonation: 'NA',
          paymentMethod: 'Free',
        });
        campOrderId = orderResp?.Camp_Order_ID || orderResp?.camp_Order_ID || orderResp?.camp_Order_Id || 0;
        campPaymentId = orderResp?.Camp_Payment_Id || orderResp?.camp_Payment_Id || 0;
        stepStatus['Create Order'] = { success: true, message: `Order #${campOrderId} created` };
      } catch (orderErr) {
        stepStatus['Create Order'] = {
          success: false,
          message: 'Order record could not be created — registration will still proceed',
          error: orderErr?.message,
        };
        console.error('CreateCampOrder error (non-blocking):', orderErr);
      }

      // Step 2: Confirm registration
      try {
        await campService.updatePaymentStatus({
          Camp_Id: parseInt(campId),
          User_Id: parseInt(userId),
          Camp_Payment_Id: campPaymentId || lastRegInfo.registrationId,
          Camp_Order_Id: campOrderId,
          Receipt_Id: 'FREE',
        });
        stepStatus['Confirmation'] = { success: true, message: 'Free registration confirmed in database' };
      } catch (confErr) {
        stepStatus['Confirmation'] = {
          success: false,
          message: 'Could not confirm registration in database — please contact support',
          error: confErr?.message,
        };
        console.error('Free confirm updatePaymentStatus error:', confErr);
      }

      await loadStudents();
      dispatch(setLastRegInfo(null));
      dispatch(clearCalculation());

      // Step 3: Send confirmation email
      try {
        await campService.sendCampConfirmationEmail({
          toEmail: userEmail,
          userName,
          campName: resolvedCampName,
          userPhone: user?.User_Phone || user?.user_Phone || '',
          registrationId: lastRegInfo.registrationId,
          amount: '0',
          paymentType: 'FREE',
          campDateRange,
        });
        stepStatus['Email'] = { success: true, message: `Confirmation sent to ${userEmail}` };
      } catch (emailErr) {
        stepStatus['Email'] = {
          success: false,
          message: 'Confirmation email could not be sent — camp staff will follow up',
          error: emailErr?.message,
        };
      }

      // Send admin alert if any step failed
      const anyFailure = Object.values(stepStatus).some((s) => !s.success);
      if (anyFailure) {
        campService.sendAdminAlertEmail({
          stepStatus,
          transactionId: 'FREE',
          campName: resolvedCampName,
          campId: parseInt(campId),
          userId: parseInt(userId),
          userName,
          userEmail,
          totalAmount: 0,
          paymentMethod: 'Free',
          timestamp,
          errorDetails: Object.entries(stepStatus)
            .filter(([, v]) => !v.success)
            .map(([k, v]) => `${k}: ${v.error || v.message}`)
            .join('\n'),
        });
      }

      setShowFreeSuccess(true);
    } catch (err) {
      console.error('Free confirm error:', err);
    } finally {
      setConfirmingFree(false);
    }
  };

  // ── Student modal ──────────────────────────────────────────────────────────
  const openAddStudentModal = () => {
    setStudentModalMode('add');
    setEditingStudent(null);
    setStudentForm({ firstName: '', lastName: '', dateOfBirth: '', gender: '', relationship: '' });
    setStudentFormErrors({});
    setShowStudentModal(true);
  };

  const openEditStudentModal = (student) => {
    setStudentModalMode('edit');
    setEditingStudent(student);
    const dob = student.student_Date_Of_Birth || student.Student_Date_Of_Birth || student.dateOfBirth || '';
    const rawGender = student.student_Gender || student.Student_Gender || student.gender || '';
    setStudentForm({
      firstName: student.student_First_Name || student.Student_First_Name || student.firstName || '',
      lastName: student.student_Last_Name || student.Student_Last_Name || student.lastName || '',
      dateOfBirth: dob ? new Date(dob).toISOString().slice(0, 10) : '',
      gender:
        rawGender === 'M' || rawGender === 'Male' ? 'Male' :
        rawGender === 'F' || rawGender === 'Female' ? 'Female' : '',
      relationship: student.student_Relationship_To_User || student.Student_Relationship_To_User || student.relationship || '',
    });
    setStudentFormErrors({});
    setShowStudentModal(true);
  };

  const validateStudentForm = () => {
    const errs = {};
    if (!studentForm.firstName.trim()) errs.firstName = 'First name is required';
    if (!studentForm.lastName.trim()) errs.lastName = 'Last name is required';
    if (!studentForm.dateOfBirth) {
      errs.dateOfBirth = 'Date of birth is required';
    } else {
      const ageMs = Date.now() - new Date(studentForm.dateOfBirth).getTime();
      const ageYears = ageMs / (1000 * 60 * 60 * 24 * 365.25);
      if (ageYears < 2) errs.dateOfBirth = 'Student must be at least 2 years old';
      else if (ageYears > 99) errs.dateOfBirth = 'Please enter a valid date of birth';
    }
    if (!studentForm.gender) errs.gender = 'Gender is required';
    return errs;
  };

  const handleSaveStudent = async () => {
    const errs = validateStudentForm();
    if (Object.keys(errs).length > 0) { setStudentFormErrors(errs); return; }
    setSavingStudent(true);
    try {
      const inputFn = studentForm.firstName.trim().toLowerCase();
      const inputLn = studentForm.lastName.trim().toLowerCase();
      const inputDob = studentForm.dateOfBirth;
      const inputGender = studentForm.gender;
      const editingId = studentModalMode === 'edit' && editingStudent ? getStudentId(editingStudent) : null;
      const isDuplicate = allStudents.some(s => {
        if (editingId && getStudentId(s) === editingId) return false;
        const fn = (s.student_First_Name || s.firstName || '').trim().toLowerCase();
        const ln = (s.student_Last_Name || s.lastName || '').trim().toLowerCase();
        const dob = s.student_Date_Of_Birth || s.Student_Date_Of_Birth || s.dateOfBirth || '';
        const dobStr = dob ? new Date(dob).toISOString().slice(0, 10) : '';
        const rawG = s.student_Gender || s.Student_Gender || s.gender || '';
        const normG = rawG === 'M' ? 'Male' : rawG === 'F' ? 'Female' : rawG;
        return fn === inputFn && ln === inputLn && dobStr === inputDob && normG === inputGender;
      });
      if (isDuplicate) {
        setSavingStudent(false);
        setStudentFormErrors({ submit: `A student named "${studentForm.firstName} ${studentForm.lastName}" with the same date of birth and gender already exists.` });
        return;
      }
      if (studentModalMode === 'edit' && editingStudent) {
        await campService.updateStudent(userId, getStudentId(editingStudent), studentForm);
      } else {
        await campService.addStudent(userId, studentForm);
      }
      await loadStudents();
      setShowStudentModal(false);
      showToastMsg(studentModalMode === 'edit' ? 'Student updated successfully!' : 'Student added successfully!', 'success');
    } catch (err) {
      const raw = err?.response?.data || err?.message || '';
      const msg = typeof raw === 'string' && raw.includes('DUPLICATE_STUDENT:')
        ? raw.split('DUPLICATE_STUDENT:')[1].trim()
        : 'Failed to save student. Please try again.';
      setStudentFormErrors({ submit: msg });
      showToastMsg(msg, 'error');
      console.error(err);
    } finally {
      setSavingStudent(false);
    }
  };

  // ── Derived totals ──────────────────────────────────────────────────────────
  const totalDue = parseFloat(
    calculation?.net_To_Pay || calculation?.netToPay || calculation?.current_Due_Total || 0
  );
  const isFreeReg = calculation && totalDue === 0;
  const canProceed = calculation && lastRegInfo;

  // ─── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-16">

      {/* Toast */}
      {showToast && (
        <div className="fixed top-4 right-4 z-[9999]">
          <Toast message={toastMessage} type={toastType} onClose={() => setShowToast(false)} duration={toastType === 'warning' ? 5000 : 3000} />
        </div>
      )}

      <div className="container-custom">
        {/* Header */}
        <div className="mb-12">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-6 mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-gray-700 mb-2">
                {selectedCamp?.camp_Name || selectedCamp?.Camp_Name || 'Camp Registration'}
              </h1>
              <p className="text-sm text-gray-500">Select attendees and camp weeks, then proceed to payment</p>
            </div>
            <div className="flex flex-wrap gap-3 md:flex-nowrap">
              <button onClick={() => navigate(-1)} className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-6 rounded-full transition shadow-md hover:shadow-lg flex items-center gap-2">
                ← Back
              </button>
              <button onClick={openAddStudentModal} className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-2 px-6 rounded-full transition-all duration-300 shadow-md hover:shadow-lg">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                Add Student
              </button>
            </div>
          </div>
          {/* Info chips */}
          <div className="flex flex-wrap gap-2 mt-3">
            {user && (
              <span className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded-full px-3 py-1 text-sm text-gray-700 shadow-sm">
                👤 {user.User_First_Name || user.user_First_Name || ''} {user.User_Last_Name || user.user_Last_Name || ''}
              </span>
            )}
            {campDates && (
              <span className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded-full px-3 py-1 text-sm text-gray-700 shadow-sm">
                📅 {formatDate(campDates.campStartDate || campDates.CampStartDate)} – {formatDate(campDates.campEndDate || campDates.CampEndDate)}
              </span>
            )}
            {weeks.length > 0 && (
              <span className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded-full px-3 py-1 text-sm text-gray-700 shadow-sm">
                🗓️ <strong>{weeks.length}</strong>&nbsp;weeks available
              </span>
            )}
          </div>
        </div>

        {/* Error */}
        {studentsError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">⚠️ {studentsError}</p>
          </div>
        )}

        {/* Student Grid */}
        {studentsLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading attendees…</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {/* Add Attendee card */}
            <div onClick={openAddStudentModal} className="bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-dashed border-blue-400 hover:border-purple-500 flex flex-col items-center justify-center min-h-[280px] transform hover:-translate-y-2 hover:scale-105 group relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10 flex flex-col items-center">
                <div className="w-28 h-28 bg-white rounded-full flex items-center justify-center mb-4 shadow-2xl border-2 border-gray-100 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                  <svg className="w-14 h-14 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                </div>
                <p className="text-gray-800 font-bold text-xl mb-1">Add New Student</p>
                <p className="text-gray-600 text-sm">Click to add a student</p>
              </div>
            </div>

            {/* Student tiles — unregistered first, registered at bottom */}
            {(() => {
              return [...allStudents].sort((a, b) => {
                const aReg = registeredStudents.some((r) => getStudentId(r) === getStudentId(a));
                const bReg = registeredStudents.some((r) => getStudentId(r) === getStudentId(b));
                return Number(aReg) - Number(bReg);
              }).map((student) => {
              const sid = getStudentId(student);
              const isReg = registeredStudents.some((r) => getStudentId(r) === sid);
              const regStudentData = registeredStudents.find((r) => getStudentId(r) === sid);
              const regWeekCount = regStudentData
                ? (regStudentData.registered_Week_Count || regStudentData.Registered_Week_Count || 0)
                : 0;
              const isFullyReg = isReg && weeks.length > 0 && regWeekCount >= weeks.length;
              const isPending = !isReg && unregisteredStudents.every((u) => getStudentId(u) !== sid);
              const gender = student.student_Gender || student.gender || '';
              const gLow = gender.toLowerCase();
              const isActive = weekModalStudent && getStudentId(weekModalStudent) === sid;
              const isSelected = selectedStudentIds.has(sid);
              return (
                <div
                  key={sid}
                  onClick={() => {
                    if (isFullyReg) { showToastMsg('All weeks are already registered for this student.', 'info'); return; }
                    toggleStudentSelection(student);
                  }}
                  className={`rounded-3xl p-6 transition-all duration-300 relative group min-h-[280px] ${
                    isReg
                      ? 'border border-emerald-200 bg-emerald-50/40 cursor-pointer hover:shadow-[0_4px_0_rgba(16,185,129,0.12),0_8px_20px_rgba(16,185,129,0.08)] hover:-translate-y-1'
                      : isSelected
                      ? 'border-2 border-amber-300 bg-white shadow-[0_8px_0_rgba(245,158,11,0.15),0_12px_32px_rgba(245,158,11,0.12),inset_0_1px_0_rgba(255,255,255,1)] -translate-y-2 scale-[1.02] cursor-pointer'
                      : isActive
                      ? 'border-2 border-indigo-300 bg-white shadow-[0_8px_0_rgba(99,102,241,0.15),0_12px_32px_rgba(99,102,241,0.12)] -translate-y-1 cursor-pointer'
                      : 'border border-gray-200 bg-white shadow-[0_2px_0_rgba(0,0,0,0.08),0_4px_16px_rgba(0,0,0,0.06),inset_0_1px_0_rgba(255,255,255,1)] hover:shadow-[0_6px_0_rgba(0,0,0,0.1),0_12px_28px_rgba(0,0,0,0.08)] hover:-translate-y-1.5 hover:border-gray-300 cursor-pointer'
                  }`}
                >
                  {/* Selected checkbox */}
                  <div className="absolute top-3 right-3 z-10">
                    {isSelected ? (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-300 to-yellow-400 flex items-center justify-center shadow-[0_3px_8px_rgba(245,158,11,0.3)] ring-2 ring-white">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      </div>
                    ) : isReg && !isActive ? (
                      <div className="bg-gray-200 text-gray-500 border border-gray-300 px-3 py-1.5 rounded-full flex items-center gap-1.5">
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                        <span className="font-semibold text-xs">Registered</span>
                      </div>
                    ) : null}
                  </div>
                  {/* Edit button — always visible, positioned below the status badge */}
                  {!isActive && !isSelected && (
                    <div className={`absolute z-10 ${isReg ? 'top-10 right-3' : 'top-3 right-3'}`}>
                      <button type="button" onClick={(e) => { e.stopPropagation(); openEditStudentModal(student); }} className="bg-blue-500 hover:bg-blue-600 rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      </button>
                    </div>
                  )}
                  {/* Gender badge */}
                  <div className="absolute top-3 left-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${gLow === 'male' || gLow === 'm' ? 'bg-blue-100 text-blue-700' : gLow === 'female' || gLow === 'f' ? 'bg-pink-100 text-pink-700' : 'bg-gray-100 text-gray-700'}`}>
                      {gLow === 'male' || gLow === 'm' ? '👦 Boy' : gLow === 'female' || gLow === 'f' ? '👧 Girl' : '👤'}
                    </span>
                  </div>
                  {/* Avatar */}
                  <div className="flex justify-center mb-4 mt-8">
                    <div className={`rounded-full border-2 ${gLow === 'male' || gLow === 'm' ? 'border-blue-400' : gLow === 'female' || gLow === 'f' ? 'border-pink-400' : 'border-gray-400'}`}>
                      <img src={getAvatarImage(gender)} alt={getStudentName(student)} className="w-28 h-28 rounded-full" onError={(e) => { e.target.src = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><circle cx="100" cy="100" r="95" fill="#f3f4f6"/><text x="100" y="145" font-size="140" text-anchor="middle" font-family="Arial">👤</text></svg>')}`; }} />
                    </div>
                  </div>
                  <p title={getStudentName(student)} className="text-center font-bold text-lg mb-3 text-gray-700 w-full px-2 break-words line-clamp-2">{getStudentName(student)}</p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isFullyReg) {
                        showToastMsg('All weeks are already registered for this student.', 'info');
                        return;
                      }
                      handleOpenWeekModal(student);
                    }}
                    className={`w-full font-semibold py-2.5 px-4 rounded-full transition-all duration-200 text-sm ${
                      isFullyReg
                        ? 'bg-gray-100 text-gray-500 border border-gray-200 cursor-default'
                        : isReg
                        ? 'bg-green-50 hover:bg-green-100 text-green-700 border border-green-300 shadow-[0_2px_0_rgba(16,185,129,0.15)] hover:shadow-[0_3px_0_rgba(16,185,129,0.2)] hover:translate-y-[-1px]'
                        : isSelected
                        ? 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 shadow-sm hover:shadow-md'
                        : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 shadow-[0_2px_0_rgba(0,0,0,0.08)] hover:shadow-[0_3px_0_rgba(0,0,0,0.1)] hover:translate-y-[-1px]'
                    }`}
                  >
                    {isFullyReg ? '✓ All Weeks Registered' : isReg ? '✓ Registered · Add More Weeks' : isSelected ? '✓ Selected · Select Weeks' : 'Select Weeks'}
                  </button>
                </div>
              );
            });})()}
          </div>
        )}

        {/* Fee Summary */}
        {calcLoading && (
          <div className="mt-10 bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-3"></div>
            <p className="text-gray-500 text-sm">Calculating fees…</p>
          </div>
        )}

        {calculation && !calcLoading && (
          <div className="mt-10 bg-white rounded-2xl shadow-xl p-8 border border-gray-100 max-w-md">
            <h2 className="text-lg font-bold text-gray-800 mb-5">🧾 Fee Summary</h2>
            <div className="space-y-3 mb-5">
              {(calculation.netFee ?? calculation.net_Fee) != null && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Registration Fee</span>
                  <span className="font-semibold">${parseFloat(calculation.netFee ?? calculation.net_Fee ?? 0).toFixed(2)}</span>
                </div>
              )}
              {parseFloat(calculation.totaPaidAmount ?? calculation.tota_Paid_Amount ?? 0) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Already Paid</span>
                  <span className="font-semibold text-green-600">−${parseFloat(calculation.totaPaidAmount ?? calculation.tota_Paid_Amount ?? 0).toFixed(2)}</span>
                </div>
              )}
            </div>
            <div className="flex justify-between items-center border-t pt-4 mb-6">
              <span className="font-bold text-gray-800">Total Due</span>
              <span className="font-black text-2xl text-indigo-600">${totalDue.toFixed(2)}</span>
            </div>
            {isFreeReg ? (
              <>
                <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3 mb-4">🎉 This registration is <strong>free</strong>! Click below to confirm.</p>
                <button onClick={handleFreeConfirm} disabled={confirmingFree || !lastRegInfo} className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:opacity-50 text-white font-bold py-3 px-6 rounded-xl transition shadow-lg">
                  {confirmingFree ? 'Confirming…' : '✓ Complete Registration (Free)'}
                </button>
              </>
            ) : (
              <button onClick={handleProceedToPayment} disabled={!canProceed} className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 text-white font-bold py-3 px-6 rounded-xl transition shadow-lg flex items-center justify-center gap-2">
                Proceed to Payment
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
              </button>
            )}
          </div>
        )}

        {!calculation && !calcLoading && allStudents.length > 0 && (
          <div className="mt-10 text-center py-8">
            <p className="text-gray-500 text-lg">📋 Click a student card to select, then use the bar below to enroll multiple at once — or click <strong>Select Weeks</strong> on any card for single enrollment.</p>
          </div>
        )}
      </div>

      {/* ─── Multi-student sticky bottom bar ─────────────────────────── */}
      {selectedStudentIds.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-amber-200 shadow-2xl z-30 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center gap-4">
            {/* Left: avatars + names — flex-1 + min-w-0 so it shrinks and lets truncate work */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="flex -space-x-3 flex-shrink-0">
                {allStudents.filter(s => selectedStudentIds.has(getStudentId(s))).slice(0, 3).map(s => {
                  const g = (s.student_Gender || s.gender || '').toLowerCase();
                  return (
                    <img key={getStudentId(s)} src={getAvatarImage(s.student_Gender || '')} alt={getStudentName(s)}
                      className={`w-9 h-9 rounded-full border-2 ${g === 'male' || g === 'm' ? 'border-blue-400' : g === 'female' || g === 'f' ? 'border-pink-400' : 'border-indigo-400'}`}
                      onError={(e) => { e.target.src = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><circle cx="100" cy="100" r="95" fill="#e0e7ff"/><text x="100" y="145" font-size="140" text-anchor="middle" font-family="Arial">👤</text></svg>')}`; }}
                    />
                  );
                })}
                {selectedStudentIds.size > 3 && (
                  <div className="w-9 h-9 rounded-full bg-indigo-100 border-2 border-indigo-300 flex items-center justify-center text-xs font-bold text-indigo-700">+{selectedStudentIds.size - 3}</div>
                )}
              </div>
              {/* min-w-0 + flex-1 here is the critical pair that enables truncate to work */}
              <div className="min-w-0 flex-1">
                <p className="font-bold text-gray-800 text-sm truncate">{selectedStudentIds.size} Student{selectedStudentIds.size > 1 ? 's' : ''} Selected</p>
                <p className="text-xs text-gray-500 truncate"
                  title={allStudents.filter(s => selectedStudentIds.has(getStudentId(s))).map(s => getStudentName(s)).join(', ')}>
                  {allStudents.filter(s => selectedStudentIds.has(getStudentId(s))).map(s => getStudentName(s)).join(', ')}
                </p>
              </div>
            </div>
            {/* Right: buttons — flex-shrink-0 so they never compress */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={clearStudentSelection} className="text-gray-500 hover:text-red-500 font-semibold text-sm transition-colors px-3 py-2 rounded-xl hover:bg-red-50">✕ Clear</button>
              <button
                onClick={handleStartBatchEnrollment}
                className="bg-white hover:bg-gray-50 text-gray-900 font-bold py-3 px-5 rounded-xl border-2 border-gray-900 shadow-[0_6px_0_rgba(0,0,0,0.85),0_8px_20px_rgba(0,0,0,0.15)] hover:shadow-[0_3px_0_rgba(0,0,0,0.85),0_4px_12px_rgba(0,0,0,0.12)] hover:translate-y-[3px] active:shadow-none active:translate-y-[6px] transition-all flex items-center gap-2 text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                Enroll {selectedStudentIds.size} Student{selectedStudentIds.size > 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Week Selection Side Drawer (2-step) ────────────────────────── */}
      {weekModalStudent && (() => {
        const newSelected = selectedWeeks.filter(w => !isPaidWeek(w));
        const subtotal = newSelected.reduce((sum, wNum) => {
          const wk = weeks.find(w => w.weekNumber === wNum);
          return sum + (wk?.fee || weekFeeFromDB);
        }, 0);
        // Per-week display fee: use each week's actual fee; average when fees differ
        const selectedWeekFees = newSelected.map(wn => weeks.find(w => w.weekNumber === wn)?.fee ?? weekFeeFromDB);
        const allSameFee = selectedWeekFees.length > 0 && selectedWeekFees.every(f => f === selectedWeekFees[0]);
        const displayFeePerWeek = allSameFee
          ? (selectedWeekFees[0] ?? weekFeeFromDB)
          : (newSelected.length > 0 ? subtotal / newSelected.length : weekFeeFromDB);
        const discountPct = newSelected.length > 1 ? 0.10 : 0;
        const discountAmt = subtotal * discountPct;
        const extTotal = [...extendedStayWeeks].filter(w => !isPaidWeek(w)).reduce((sum, wNum) => {
          const wk = weeks.find(w => w.weekNumber === wNum);
          return sum + (wk?.extFee || extFeeFromDB);
        }, 0);
        const grandTotal = subtotal - discountAmt + extTotal;

        return (
          <>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 animate-fadeIn" onClick={handleCloseWeekModal} />
            <div className="fixed top-0 right-0 h-full w-full md:w-[520px] bg-gradient-to-b from-slate-50 to-white shadow-2xl z-50 flex flex-col animate-slideInRight">

              {/* Drawer header */}
              <div className="bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {drawerStep === 'summary' && (
                    <button onClick={() => setDrawerStep('select')} className="text-gray-400 hover:text-gray-700 p-1.5 rounded-lg hover:bg-gray-100 transition mr-1">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                  )}
                  <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-2">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-gray-800">{drawerStep === 'select' ? 'Select Camp Weeks' : 'Enrollment Summary'}</h2>
                    <p className="text-xs text-gray-500 flex items-center gap-1 min-w-0">
                      {studentQueue.length > 1 && (
                        <span className="bg-indigo-100 text-indigo-700 font-semibold px-2 py-0.5 rounded-full flex-shrink-0">{currentQueueIdx + 1}/{studentQueue.length}</span>
                      )}
                      <span className="flex-shrink-0">Enrolling for</span>
                      <span
                        className="font-semibold text-indigo-700"
                        title={getStudentName(weekModalStudent)}
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140, display: 'inline-block' }}>
                        {getStudentName(weekModalStudent)}
                      </span>
                    </p>
                  </div>
                </div>
                {/* Step indicator */}
                <div className="flex items-center gap-2 mr-4">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${drawerStep === 'select' ? 'bg-indigo-600 text-white' : 'bg-green-500 text-white'}`}>1</div>
                  <div className="w-6 h-0.5 bg-gray-200"></div>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${drawerStep === 'summary' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'}`}>2</div>
                </div>
                <button onClick={handleCloseWeekModal} className="text-gray-400 hover:text-gray-700 p-2 rounded-xl hover:bg-gray-100 transition hover:rotate-90 hover:scale-110">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              {/* ── STEP 1: Select Weeks ── */}
              {drawerStep === 'select' && (
                <>
                  {/* Info banner */}
                  <div className="mx-4 mt-4 bg-white border border-gray-200 rounded-2xl shadow-[0_2px_0_rgba(0,0,0,0.06),0_4px_12px_rgba(0,0,0,0.05)] p-4 flex flex-wrap gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-[120px]">
                      <span className="w-9 h-9 bg-indigo-50 rounded-xl flex items-center justify-center text-base flex-shrink-0">💰</span>
                      <div>
                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Camp Fee</p>
                        <p className="text-base font-black text-gray-800">${weekFeeFromDB}<span className="text-xs font-medium text-gray-400">/week</span></p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-1 min-w-[120px]">
                      <span className="w-9 h-9 bg-orange-50 rounded-xl flex items-center justify-center text-base flex-shrink-0">🏕️</span>
                      <div>
                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Extended Stay</p>
                        <p className="text-base font-black text-gray-800">+${extFeeFromDB}<span className="text-xs font-medium text-gray-400">/week</span></p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-1 min-w-[120px]">
                      <span className="w-9 h-9 bg-green-50 rounded-xl flex items-center justify-center text-base flex-shrink-0">🏷️</span>
                      <div>
                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Multi-week</p>
                        <p className="text-base font-black text-gray-800">10% <span className="text-xs font-medium text-gray-400">off (2+)</span></p>
                      </div>
                    </div>
                  </div>

                  {/* Week list */}
                  <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                    {loadingRecords ? (
                      <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-3"></div>
                        <p className="text-gray-500 text-sm">Loading camp schedule…</p>
                      </div>
                    ) : weeks.length === 0 ? (
                      <p className="text-gray-500 text-sm text-center py-8">Camp schedule not available yet.</p>
                    ) : (
                      (() => {
                        const unpaidWeeks = weeks.filter((w) => !isPaidWeek(w.weekNumber));
                        const paidWeeks   = weeks.filter((w) =>  isPaidWeek(w.weekNumber));

                        const renderWeekCard = (w) => {
                          const paid     = isPaidWeek(w.weekNumber);
                          const expired  = w.isExpired && !paid;
                          const selected = selectedWeeks.includes(w.weekNumber);
                          const hasExt   = extendedStayWeeks.has(w.weekNumber);
                          return (
                            <div key={w.weekNumber} className={`rounded-2xl overflow-hidden transition-all duration-300 ${
                              paid     ? 'border border-gray-200 bg-gray-50/60' :
                              expired  ? 'border border-gray-200 opacity-50' :
                              selected ? 'border-2 border-gray-300 bg-white shadow-[0_6px_0_rgba(0,0,0,0.12),0_12px_28px_rgba(0,0,0,0.1),inset_0_1px_0_rgba(255,255,255,1)] -translate-y-1.5 scale-[1.015]' :
                                         'border border-gray-200 shadow-[0_2px_0_rgba(0,0,0,0.08),0_4px_12px_rgba(0,0,0,0.05),inset_0_1px_0_rgba(255,255,255,1)] cursor-pointer hover:shadow-[0_4px_0_rgba(0,0,0,0.1),0_8px_20px_rgba(0,0,0,0.07)] hover:-translate-y-0.5 hover:border-gray-300'
                            }`}>
                              {/* Week card main row */}
                              <div
                                onClick={() => !paid && !expired && toggleWeek(w.weekNumber)}
                                className={`flex items-center gap-3 p-4 ${
                                  paid     ? 'bg-gray-50 cursor-default' :
                                  expired  ? 'bg-gray-50 cursor-not-allowed' :
                                  selected ? 'bg-white' :
                                             'bg-white'
                                }`}
                              >
                                {/* Checkbox */}
                                <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                                  paid     ? 'bg-gray-400 border-0' :
                                  selected ? 'bg-gray-700 border-0 shadow-[0_2px_8px_rgba(0,0,0,0.2)]' :
                                             'border-2 border-gray-300 bg-white'
                                }`}>
                                  {(paid || selected) && (
                                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                  )}
                                </div>
                                {/* Week number badge */}
                                <div className={`w-11 h-11 rounded-xl flex flex-col items-center justify-center flex-shrink-0 font-black text-sm leading-none ${
                                  paid     ? 'bg-gray-200 text-gray-400' :
                                  selected ? 'bg-gray-700 text-white shadow-[0_3px_10px_rgba(0,0,0,0.2)]' :
                                  expired  ? 'bg-gray-200 text-gray-400' :
                                             'bg-gradient-to-br from-gray-100 to-gray-200 text-gray-700'
                                }`}>
                                  <span className="text-[10px] font-semibold opacity-70 leading-none mb-0.5">WK</span>
                                  <span>{w.weekNumber}</span>
                                </div>
                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                  <p className={`font-bold text-sm truncate ${paid ? 'text-gray-500' : 'text-gray-700'}`} title={w.label}>{w.label}</p>
                                  <p className={`text-xs flex items-center gap-1 mt-0.5 truncate ${paid ? 'text-gray-400' : 'text-gray-500'}`}>
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                    {w.dateRange}
                                  </p>
                                </div>
                                {/* Price / status */}
                                <div className="text-right flex-shrink-0">
                                  {paid ? (
                                    <span className="inline-flex items-center gap-1.5 bg-gray-400 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                      Paid
                                    </span>
                                  ) : expired ? (
                                    <span className="text-gray-400 text-xs font-semibold">Expired</span>
                                  ) : (
                                    <div className="flex flex-col items-end">
                                      <p className={`font-black text-base ${selected ? 'text-gray-800' : 'text-gray-700'}`}>${w.fee}</p>
                                      <p className="text-xs text-gray-400">/week</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                              {/* Extended stay — paid (read-only, gray) */}
                              {paid && hasExt && (
                                <div className="flex items-center gap-3 px-4 py-3 border-t border-gray-200 bg-gray-100/50 cursor-default">
                                  <span className="text-base flex-shrink-0 grayscale opacity-60">🏕️</span>
                                  <div className="flex-1">
                                    <p className="text-sm font-semibold text-gray-500">Extended Stay</p>
                                    <p className="text-xs text-gray-400">Included in this registration</p>
                                  </div>
                                  <span className="inline-flex items-center gap-1 bg-gray-400 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                    Paid
                                  </span>
                                </div>
                              )}
                              {selected && !paid && (
                                      <div
                                        onClick={() => toggleExtendedStay(w.weekNumber)}
                                        className={`flex items-center gap-3 px-4 py-3 border-t-2 cursor-pointer transition-all duration-200 ${
                                          hasExt ? 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200' : 'bg-gray-50 border-gray-100 hover:bg-orange-50'
                                        }`}
                                      >
                                        <span className="text-lg flex-shrink-0">🏕️</span>
                                        <div className="flex-1">
                                          <p className="text-sm font-semibold text-gray-700">Extended Stay</p>
                                          <p className="text-xs text-gray-500">Additional accommodation for this week</p>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                          <span className="text-sm font-bold text-orange-600">+${w.extFee ?? extFeeFromDB}</span>
                                          <div className={`w-10 h-6 rounded-full transition-all duration-200 flex items-center ${hasExt ? 'bg-orange-500 justify-end' : 'bg-gray-300 justify-start'}`}>
                                            <div className="w-5 h-5 bg-white rounded-full mx-0.5 shadow-sm"></div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              };

                              // ── Unpaid weeks by month ──
                              const byMonth = {};
                              unpaidWeeks.forEach((w) => {
                                if (!byMonth[w.month]) byMonth[w.month] = [];
                                byMonth[w.month].push(w);
                              });

                              return (
                                <>
                                  {Object.entries(byMonth).map(([month, mWeeks]) => (
                                    <div key={month}>
                                      <div className="flex items-center gap-2 mb-3">
                                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-indigo-200 to-transparent" />
                                        <span className="text-xs font-bold text-indigo-500 uppercase tracking-widest bg-indigo-50 px-3 py-1 rounded-full border border-indigo-100">{month}</span>
                                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-indigo-200 to-transparent" />
                                      </div>
                                      <div className="space-y-3">{mWeeks.map(renderWeekCard)}</div>
                                    </div>
                                  ))}

                                  {/* ── Paid weeks at bottom ── */}
                                  {paidWeeks.length > 0 && (
                                    <div className="mt-4">
                                      <div className="flex items-center gap-2 mb-3">
                                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
                                        <span className="text-xs font-bold text-gray-400 uppercase tracking-widest bg-gray-50 px-3 py-1 rounded-full border border-gray-200">Already Registered</span>
                                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
                                      </div>
                                      <div className="space-y-3">{paidWeeks.map(renderWeekCard)}</div>
                                    </div>
                                  )}
                                </>
                              );
                            })()
                    )}
                    {weekSaveError && (
                      <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                        <p className="text-red-700 text-sm">{weekSaveError}</p>
                      </div>
                    )}
                  </div>

                  {/* Step 1 footer: live price + Next */}
                  <div className="bg-white border-t border-gray-100 px-4 pt-3 pb-4">
                    {newSelected.length > 0 && (
                      <div className="mb-3">
                        {/* Line items */}
                        <div className="space-y-2 px-1 mb-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 font-semibold">{newSelected.length} week{newSelected.length > 1 ? 's' : ''} × ${allSameFee ? displayFeePerWeek : displayFeePerWeek.toFixed(2)}</span>
                            <span className="text-sm font-bold text-gray-700">${subtotal.toFixed(2)}</span>
                          </div>
                          {discountAmt > 0 && (
                            <div className="flex justify-between items-center">
                              <span className="flex items-center gap-1.5">
                                <span className="bg-gray-800 text-white text-xs font-bold px-2 py-0.5 rounded-md">10% OFF</span>
                                <span className="text-sm text-gray-600 font-medium">Multi-week discount</span>
                              </span>
                              <span className="text-sm font-bold text-gray-700">−${discountAmt.toFixed(2)}</span>
                            </div>
                          )}
                          {[...extendedStayWeeks].filter(w => !isPaidWeek(w)).length > 0 && (
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600 font-medium flex items-center gap-1">🏕️ Extended stay × {[...extendedStayWeeks].filter(w => !isPaidWeek(w)).length}</span>
                              <span className="text-sm font-bold text-gray-700">+${extTotal.toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                        {/* Total row */}
                        <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex justify-between items-center shadow-[0_2px_0_rgba(0,0,0,0.06)]">
                          <span className="text-gray-600 text-sm font-semibold">Estimated Total</span>
                          <span className="text-gray-800 font-black text-xl tracking-tight">${grandTotal.toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2.5">
                      <button onClick={handleCloseWeekModal} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-600 font-semibold py-3 rounded-xl transition text-sm">Cancel</button>
                      <button
                        onClick={() => {
                          if (selectedWeeks.length === 0) { setWeekSaveError('Please select at least one week.'); return; }
                          setWeekSaveError(null);
                          setDrawerStep('summary');
                        }}
                        disabled={selectedWeeks.length === 0}
                        className="flex-[2] bg-white hover:bg-gray-50 disabled:opacity-40 text-gray-800 font-bold py-3 px-6 rounded-xl border-2 border-gray-300 shadow-[0_4px_0_rgba(0,0,0,0.1),0_4px_12px_rgba(0,0,0,0.06)] hover:shadow-[0_2px_0_rgba(0,0,0,0.1)] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] transition-all flex items-center justify-center gap-2 text-sm"
                      >
                        Next
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* ── STEP 2: Enrollment Summary ── */}
              {drawerStep === 'summary' && (
                <>
                  <div className="flex-1 overflow-y-auto px-4 py-4">
                    {/* Student info */}
                    <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 mb-5 flex items-center gap-4 overflow-hidden">
                      <img
                        src={getAvatarImage(weekModalStudent?.student_Gender || weekModalStudent?.gender || '')}
                        alt=""
                        className="w-14 h-14 rounded-full border-2 border-gray-200 flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <p className="font-black text-gray-900 text-base truncate" title={getStudentName(weekModalStudent)}>{getStudentName(weekModalStudent)}</p>
                        <p className="text-gray-500 text-sm truncate">{weekModalStudent?.student_Relationship_To_User || weekModalStudent?.relationship || 'Attendee'}</p>
                      </div>
                    </div>

                    {/* Selected weeks table */}
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Selected Weeks</p>
                    <div className="space-y-2 mb-5">
                      {selectedWeeks.sort((a, b) => a - b).map((wn) => {
                        const w = weeks.find((x) => x.weekNumber === wn);
                        if (!w) return null;
                        const paid = isPaidWeek(wn);
                        const hasExt = extendedStayWeeks.has(wn);
                        return (
                          <div key={wn} className={`rounded-xl p-3 border shadow-[0_2px_0_rgba(0,0,0,0.06),0_3px_8px_rgba(0,0,0,0.04),inset_0_1px_0_rgba(255,255,255,1)] ${paid ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm flex-shrink-0 ${paid ? 'bg-green-100 text-green-700' : 'bg-gray-700 text-white shadow-[0_2px_6px_rgba(0,0,0,0.18)]'}`}>{wn}</div>
                                <div className="min-w-0">
                                  <p className="font-semibold text-gray-800 text-sm truncate" title={w.label}>{w.label}</p>
                                  <p className="text-gray-500 text-xs truncate">{w.dateRange}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                {paid ? (
                                  <span className="text-green-600 text-xs font-bold">✓ Paid</span>
                                ) : (
                                  <p className="font-bold text-gray-800">${w.fee}</p>
                                )}
                                {!paid && hasExt && <p className="text-orange-600 text-xs font-semibold">+${extFeeFromDB} stay</p>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Price breakdown */}
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Price Breakdown</p>
                    <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200 shadow-[0_2px_0_rgba(0,0,0,0.06),inset_0_1px_0_rgba(255,255,255,1)] space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Camp fee ({newSelected.length} week{newSelected.length > 1 ? 's' : ''} × ${allSameFee ? displayFeePerWeek : displayFeePerWeek.toFixed(2)})</span>
                        <span className="font-semibold text-gray-800">${subtotal.toFixed(2)}</span>
                      </div>
                      {discountAmt > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-green-700 font-medium flex items-center gap-1">
                            <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs">10% OFF</span>
                            Multi-week discount
                          </span>
                          <span className="font-semibold text-green-600">−${discountAmt.toFixed(2)}</span>
                        </div>
                      )}
                      {(() => { const ec = [...extendedStayWeeks].filter(w => !isPaidWeek(w)).length; return ec > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-orange-600 font-medium flex items-center gap-1">
                            🏕️ Extended stay ({ec} week{ec > 1 ? 's' : ''} × ${extFeeFromDB})
                          </span>
                          <span className="font-semibold text-orange-600">+${extTotal.toFixed(2)}</span>
                        </div>
                      ); })()}
                      <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                        <span className="font-black text-gray-900 text-base">Total Amount</span>
                        <span className="font-black text-2xl text-gray-900">${grandTotal.toFixed(2)}</span>
                      </div>
                    </div>

                    {weekSaveError && (
                      <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-3">
                        <p className="text-red-700 text-sm">{weekSaveError}</p>
                      </div>
                    )}
                  </div>

                  {/* Step 2 footer */}
                  <div className="bg-white border-t border-gray-100 px-4 py-4 flex gap-3">
                    <button onClick={() => setDrawerStep('select')} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-xl transition flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                      Back
                    </button>
                    <button
                      onClick={handleSaveWeeks}
                      disabled={savingWeeks}
                      className="flex-[2] bg-white hover:bg-gray-50 disabled:opacity-40 text-gray-800 font-bold py-3 px-4 rounded-xl border-2 border-gray-300 shadow-[0_4px_0_rgba(0,0,0,0.1),0_4px_12px_rgba(0,0,0,0.06)] hover:shadow-[0_2px_0_rgba(0,0,0,0.1)] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] transition-all flex items-center justify-center gap-2"
                    >
                      {savingWeeks ? (
                        <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700"></div> Saving…</>
                      ) : (
                        <><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg> Confirm &amp; Continue</>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        );
      })()}

      {/* ─── Add/Edit Student Modal — exact copy of EnrollStudent ────────── */}
      {showStudentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-slideInUp">
            {/* Modal header */}
            <div className="bg-gradient-to-b from-white to-gray-50 px-8 py-6 relative" style={{ boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-500 rounded-2xl blur-xl opacity-60 animate-pulse"></div>
                    <div className="relative bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-2xl p-3" style={{ boxShadow: '0 8px 16px -4px rgba(59,130,246,0.6)' }}>
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    </div>
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-gray-900 tracking-tight">{studentModalMode === 'add' ? 'Add New Student' : 'Edit Student'}</h2>
                    <p className="text-xs text-gray-500 mt-0.5 font-medium">{studentModalMode === 'add' ? 'Enter student details below' : 'Update student information'}</p>
                  </div>
                </div>
                <button onClick={() => setShowStudentModal(false)} className="group text-gray-400 hover:text-gray-700 rounded-xl p-2.5 transition-all duration-300 transform hover:rotate-90 hover:scale-110">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            {/* Form */}
            <div className="p-6 space-y-5">
              {/* First Name */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  First Name <span className="text-red-500">*</span>
                </label>
                <input type="text" value={studentForm.firstName} onChange={(e) => setStudentForm((p) => ({ ...p, firstName: e.target.value.replace(/[^a-zA-Z\s]/g, '') }))} placeholder="Enter first name" maxLength="100"
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 ${studentFormErrors.firstName ? 'border-red-500 focus:ring-red-500 bg-red-50' : 'border-gray-200 focus:border-blue-500 focus:ring-blue-200 hover:border-blue-300'}`} />
                {studentFormErrors.firstName && <p className="text-red-500 text-xs mt-1">{studentFormErrors.firstName}</p>}
              </div>
              {/* Last Name */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input type="text" value={studentForm.lastName} onChange={(e) => setStudentForm((p) => ({ ...p, lastName: e.target.value.replace(/[^a-zA-Z\s]/g, '') }))} placeholder="Enter last name" maxLength="100"
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 ${studentFormErrors.lastName ? 'border-red-500 focus:ring-red-500 bg-red-50' : 'border-gray-200 focus:border-purple-500 focus:ring-purple-200 hover:border-purple-300'}`} />
                {studentFormErrors.lastName && <p className="text-red-500 text-xs mt-1">{studentFormErrors.lastName}</p>}
              </div>
              {/* Date of Birth */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  Date Of Birth <span className="text-red-500">*</span>
                </label>
                <input type="date" value={studentForm.dateOfBirth}
                  min={new Date(new Date().setFullYear(new Date().getFullYear() - 99)).toISOString().split('T')[0]}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 2)).toISOString().split('T')[0]}
                  onChange={(e) => setStudentForm((p) => ({ ...p, dateOfBirth: e.target.value }))}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 ${studentFormErrors.dateOfBirth ? 'border-red-500 focus:ring-red-500 bg-red-50' : 'border-gray-200 focus:border-pink-500 focus:ring-pink-200 hover:border-pink-300'}`} />
                {studentFormErrors.dateOfBirth && <p className="text-red-500 text-xs mt-1 font-semibold">{studentFormErrors.dateOfBirth}</p>}
              </div>
              {/* Gender */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                  Gender <span className="text-red-500">*</span>
                </label>
                <select value={studentForm.gender} onChange={(e) => setStudentForm((p) => ({ ...p, gender: e.target.value }))}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 appearance-none bg-white ${studentFormErrors.gender ? 'border-red-500 focus:ring-red-500 bg-red-50' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200 hover:border-indigo-300'}`}>
                  <option value="">Select gender</option>
                  <option value="Male">👦 Male</option>
                  <option value="Female">👧 Female</option>
                </select>
                {studentFormErrors.gender && <p className="text-red-500 text-xs mt-1">{studentFormErrors.gender}</p>}
              </div>
              {/* Relationship */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  Relationship
                </label>
                <select value={studentForm.relationship} onChange={(e) => setStudentForm((p) => ({ ...p, relationship: e.target.value }))}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 appearance-none bg-white ${studentFormErrors.relationship ? 'border-red-500 focus:ring-red-500 bg-red-50' : 'border-gray-200 focus:border-green-500 focus:ring-green-200 hover:border-green-300'}`}>
                  <option value="">Select relationship</option>
                  <option value="Self">🙋 Self</option>
                  <option value="Parent">👨‍👩‍👧 Parent</option>
                  <option value="Legal Guardian">👤 Legal Guardian</option>
                </select>
                {studentFormErrors.relationship && <p className="text-red-500 text-xs mt-1">{studentFormErrors.relationship}</p>}
              </div>
              {studentFormErrors.submit && <p className="text-red-500 text-sm text-center">{studentFormErrors.submit}</p>}
            </div>

            {/* Modal footer */}
            <div className="px-6 pb-6 pt-2 flex gap-3">
              <button onClick={() => setShowStudentModal(false)} className="flex-1 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200 shadow-md flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                Close
              </button>
              <button onClick={handleSaveStudent} disabled={savingStudent} className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:opacity-50 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                {savingStudent ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Free Registration Success Modal ─────────────────────────────── */}
      {showFreeSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-10 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
            </div>
            <h2 className="text-2xl font-black text-gray-900 mb-3">Registration Confirmed!</h2>
            <p className="text-gray-600 text-sm mb-8">Your camp registration is complete. A confirmation email has been sent to your email address.</p>
            <button onClick={() => { dispatch(clearCampData()); setShowFreeSuccess(false); navigate('/camp/center'); }} className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-bold py-3 px-6 rounded-xl transition shadow-lg">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampRegistration;
